import type { FC, RefObject } from 'react';
import React, { useState, useEffect } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import WorldIcon from '@atlaskit/icon/core/globe';
import type { NewCoreIconProps } from '@atlaskit/icon';
import { layers } from '@atlaskit/theme/constants';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import Link from '@atlaskit/link';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import { FeedbackFlag } from '@atlaskit/feedback-collector';
import { FlagGroup } from '@atlaskit/flag';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { SpotlightManager, SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';

import { isFedRamp } from '@atlassian/atl-context';

import { CopyLinkButton, type PageMode } from '@confluence/copy-content-link';
import { fg } from '@confluence/feature-gating';
import {
	useSetOnboardingState,
	useGetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { ONBOARDING_SHARE_AND_RESTRICT_DIALOG } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useChoreographerAssets } from '@confluence/choreographer-services';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../controller/Controller';
import { WorldGreenDotGlyph } from '../../../svg/WorldGreenDotIcon-svg';
import PublicLinkButtonSpotlight from '../../../svg/PublicLinkButtonSpotlight.svg';
import { useDelay } from '../useDelay';

import { USDFeedbackCollector } from './USDFeedbackCollector';
import { SlackIcon } from './icons/SlackIconColorized';

const EDIT_PUBLIC_LINK_SPOTLIGHT_KEY =
	ONBOARDING_SHARE_AND_RESTRICT_DIALOG.PUBLIC_LINK_FOOTER_BUTTON_SEEN;

const FooterStyles = xcss({
	boxSizing: 'border-box',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
	paddingLeft: 'space.250',
	paddingRight: 'space.250',
	borderTop: `1px solid ${token('color.border')}`,
});

const InfoPopupStyles = xcss({
	width: '315px',
	justifyContent: 'center',
	padding: 'space.200',
});

const InfoPopupTextStyles = xcss({
	font: 'font.body',
	color: 'color.text.subtle',
	paddingBottom: 'space.150',
});

const InfoBoxStyle = xcss({
	paddingTop: 'space.025',
});

const i18n = defineMessages({
	copyLink: {
		id: 'share-and-restrict-dialog.footer.copy-link',
		defaultMessage: 'Copy link',
		description: 'Text for the share and restrict dialog footer button that copies a link',
	},
	publicLink: {
		id: 'share-and-restrict-dialog.footer.public-link',
		defaultMessage: 'Public link',
		description:
			'Text for the share and restrict dialog footer button that opens the Public Link dialog',
	},
	publicLinkIsOn: {
		id: 'share-and-restrict-dialog.footer.public-link-is-on',
		defaultMessage: 'Public link is on',
		description:
			'Text for the share and restrict dialog footer button that opens the Public Link dialog, and also signifies that public link is on/enabled for this content',
	},
	slack: {
		id: 'share-and-restrict-dialog.footer.slack',
		defaultMessage: 'Slack',
		description:
			'Text for the share and restrict dialog footer button that opens a slack sharing dialog',
	},
	EAPLozengeText: {
		id: 'share-and-restrict-dialog.footer.eap-lozenge',
		defaultMessage: 'EARLY ACCESS',
		description: 'Text for a lozenge showing that feature is early access',
	},
	infoPopupText: {
		id: 'share-and-restrict-dialog.footer.info-popup-text',
		defaultMessage:
			'The new Share experience is currently in early access. Your feedback is invaluable. Let us know what you think!',
		description: 'Tool tip regarding EAP and providing feedback',
	},
	giveFeedbackLink: {
		id: 'share-and-restrict-dialog.give-feedback-icon',
		defaultMessage: 'Give feedback',
		description: 'Text for label for opening feedback collector',
	},
	editPublicWhiteboardSpotlightText: {
		id: 'share-and-restrict-dialog.edit-public-whiteboard-spotlight-text',
		defaultMessage:
			'Allow teammates that don’t have a Confluence account to view or edit your whiteboards.',
		description:
			'Spotlight text for guiding users to the edit whiteboard permission within Share and Restrict dialog',
	},
	editPublicWhiteboardSpotlightOkay: {
		id: 'share-and-restrict-dialog.edit-public-whiteboard-spotlight-okay',
		defaultMessage: 'Okay',
		description:
			'Action Okay that will be within the spotlight to close the spotlight when clicked',
	},
});

export const EAPLozenge: FC = () => {
	const intl = useIntl();
	return <Lozenge appearance="default">{intl.formatMessage(i18n.EAPLozengeText)}</Lozenge>;
};

export type DistributionFooterProps = {
	contentId: string;
	contentType: string;
	spaceKey: string;
	pageMode: PageMode;
	isLockedOut: boolean;
	isUnpublishedDraft: boolean;
	isLivePage: boolean;
	onSlackClick: () => void;
	onPublicLinkClick: () => void;
	isPublicLinkOn: boolean;
	isPublicLinkEditAllowedForContentType: boolean;
	lastTriggeredDialogVariant: 'publicLink' | 'shareToSlack' | 'accessRequests' | null;
	publicLinkButtonRef: RefObject<HTMLButtonElement>;
	shareToSlackButtonRef: RefObject<HTMLButtonElement>;
	clearElementToFocus: () => void;
};

export const DistributionFooter = ({
	contentId,
	contentType,
	spaceKey,
	pageMode,
	isLockedOut,
	isUnpublishedDraft,
	isLivePage,
	onSlackClick,
	onPublicLinkClick,
	isPublicLinkOn,
	isPublicLinkEditAllowedForContentType,
	lastTriggeredDialogVariant,
	publicLinkButtonRef,
	shareToSlackButtonRef,
	clearElementToFocus,
}: DistributionFooterProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isInfoPopupOpen, setIsInfoPopupOpen] = useState<boolean>(false);
	const [isFeedbackCollectorOpen, setIsFeedbackCollectorOpen] = useState(false);
	const [displayFeedbackCollectorFlag, setDisplayFeedbackCollectorFlag] = useState(false);

	const isFedRampEnv = isFedRamp();
	const shouldShowPublicLinkButton =
		!isUnpublishedDraft && (contentType === 'page' || contentType === 'whiteboard');

	// Introducing a delay in showing the spotlights to allow dialog UI to settle and avoid misalignments
	const isDelayOver = useDelay(3000);

	// Spotlight
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);

	const {
		data,
		loading,
		error: spotlightError,
	} = useGetOnboardingState(EDIT_PUBLIC_LINK_SPOTLIGHT_KEY);
	const { setOnboardingState } = useSetOnboardingState();

	useEffect(() => {
		if (
			shouldShowPublicLinkButton &&
			isPublicLinkEditAllowedForContentType &&
			data &&
			!loading &&
			!spotlightError
		) {
			const spotlightSeen =
				data?.onboardingState?.find((prop) => prop.key === EDIT_PUBLIC_LINK_SPOTLIGHT_KEY)
					?.value === 'true';

			// If the spotlight hasn't been seen before
			if (!spotlightSeen && isDelayOver) {
				setIsSpotlightActive(true);
			}
		} else if (spotlightError && !loading) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					actionSubject: 'PublicLinksButton',
					action: 'spotlight failed',
					attributes: {
						contentId,
						contentType,
						isPublicLinkOn,
					},
				},
			}).fire();
		}
	}, [
		data,
		loading,
		spotlightError,
		shouldShowPublicLinkButton,
		isPublicLinkEditAllowedForContentType,
		contentId,
		contentType,
		isPublicLinkOn,
		createAnalyticsEvent,
		isDelayOver,
	]);

	useEffect(() => {
		if (!lastTriggeredDialogVariant) {
			return;
		}

		let elementWasFocused = false;

		if (lastTriggeredDialogVariant === 'publicLink' && !!publicLinkButtonRef?.current) {
			publicLinkButtonRef.current.focus();
			elementWasFocused = true;
		} else if (lastTriggeredDialogVariant === 'shareToSlack' && !!shareToSlackButtonRef?.current) {
			shareToSlackButtonRef.current.focus();
			elementWasFocused = true;
		}

		if (elementWasFocused) {
			clearElementToFocus();
		}
	}, [lastTriggeredDialogVariant, publicLinkButtonRef, shareToSlackButtonRef, clearElementToFocus]);

	const endSpotlight = () => {
		// Only show spotlight once
		void setOnboardingState({
			key: EDIT_PUBLIC_LINK_SPOTLIGHT_KEY,
			value: 'true',
		});
		setIsSpotlightActive(false);
	};

	const { Spotlight } = useChoreographerAssets();

	const iconProps: Partial<NewCoreIconProps> = {
		label: '',
		...(isPublicLinkOn ? { secondaryColor: token('color.icon.accent.green') } : {}),
	};
	const Icon = isPublicLinkOn ? WorldGreenDotGlyph : WorldIcon;

	const handleSlackClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'ShareToSlackFooterButton',
				attributes: {
					contentId,
					contentType,
					isLivePage,
					pageMode,
				},
			},
		}).fire();
		onSlackClick();
	};
	/* eslint-disable jsx-a11y/anchor-is-valid*/
	return (
		<Inline grow="fill" spread="space-between" alignBlock="center" xcss={FooterStyles}>
			<Inline space="space.100">
				<CopyLinkButton
					buttonAppearance="subtle"
					contentId={contentId}
					contentType={contentType}
					spaceKey={spaceKey}
					pageMode={pageMode}
					isDraft={isUnpublishedDraft}
					isLivePage={isLivePage}
					source={SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE}
					buttonText={intl.formatMessage(i18n.copyLink)}
					isDisabled={isLockedOut}
				/>

				{!isFedRampEnv && !isUnpublishedDraft && (
					<Button
						appearance="subtle"
						iconBefore={SlackIcon}
						onClick={handleSlackClick}
						isDisabled={isLockedOut}
						ref={shareToSlackButtonRef}
					>
						{intl.formatMessage(i18n.slack)}
					</Button>
				)}

				{shouldShowPublicLinkButton && (
					<SpotlightManager>
						<SpotlightTarget name="publicLinksButton">
							<Button
								appearance="subtle"
								iconBefore={(parentProps) => <Icon {...parentProps} {...iconProps} />}
								onClick={onPublicLinkClick}
								isDisabled={isLockedOut}
								ref={publicLinkButtonRef}
							>
								{isPublicLinkOn
									? intl.formatMessage(i18n.publicLinkIsOn)
									: intl.formatMessage(i18n.publicLink)}
							</Button>
						</SpotlightTarget>

						<SpotlightTransition>
							{isSpotlightActive && (
								<Spotlight
									messageType="engagement"
									messageId="publicLinksButton"
									actions={[
										{
											onClick: endSpotlight,
											text: intl.formatMessage(i18n.editPublicWhiteboardSpotlightOkay),
										},
									]}
									image={PublicLinkButtonSpotlight}
									target="publicLinksButton"
									key="shareAndRestrict-publicLink-spotlight"
									targetRadius={3}
									targetBgColor={token('elevation.surface')}
									dialogPlacement="bottom left"
									dialogWidth={300}
								>
									<FormattedMessage {...i18n.editPublicWhiteboardSpotlightText} />
								</Spotlight>
							)}
						</SpotlightTransition>
					</SpotlightManager>
				)}
			</Inline>
			{fg('confluence_frontend_eap_unified_restrict_and_share') && (
				<Inline alignBlock="center" space="space.050" xcss={InfoBoxStyle}>
					<Inline alignBlock="center">
						<EAPLozenge />
					</Inline>
					<Box>
						<Popup
							trigger={(triggerProps) => (
								<IconButton
									{...triggerProps}
									shape="circle"
									spacing="compact"
									appearance="subtle"
									icon={StatusInformationIcon}
									label=""
									onClick={() => {
										setIsInfoPopupOpen((prevValue) => !prevValue);
									}}
									data-testId="share-and-restrict-dialog.info-icon"
								/>
							)}
							zIndex={layers.modal()}
							placement="bottom"
							isOpen={isInfoPopupOpen}
							onClose={() => setIsInfoPopupOpen(false)}
							content={() => (
								<Box xcss={InfoPopupStyles}>
									<Box xcss={InfoPopupTextStyles}>{intl.formatMessage(i18n.infoPopupText)}</Box>
									<Box>
										<Link
											href="#"
											appearance="default"
											onClick={() => {
												setIsFeedbackCollectorOpen(true);
											}}
										>
											<Inline space="space.100" alignBlock="center">
												<FeedbackIcon label="" />
												{intl.formatMessage(i18n.giveFeedbackLink)}
											</Inline>
										</Link>
										{isFeedbackCollectorOpen && (
											<USDFeedbackCollector
												contentId={contentId}
												onClose={() => {
													setIsFeedbackCollectorOpen(false);
													setIsInfoPopupOpen(false);
												}}
												onSubmit={() => setDisplayFeedbackCollectorFlag(true)}
											/>
										)}
									</Box>
								</Box>
							)}
						/>
					</Box>
				</Inline>
			)}
			<FlagGroup onDismissed={() => setDisplayFeedbackCollectorFlag(false)}>
				{displayFeedbackCollectorFlag && <FeedbackFlag />}
			</FlagGroup>
		</Inline>
	);
};
